/**
 * Modal de historial de transacciones por cuenta.
 *
 * @taskId T474
 * @see functional/Specs/specs_cycle_4.md HU-27-FE §Pantalla 2
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownCircle, ArrowUpCircle, Search } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@flexibilitypay/ui';

import { useTransactionHistory } from '../hooks/use-accounts';
import type {
  AccountSummary,
  TransactionStatus,
  TransactionSummary,
  TransactionType,
} from '../types/account.types';

export interface AccountHistoryModalProps {
  account: AccountSummary | null;
  open: boolean;
  onClose: () => void;
  onViewTransaction: (transaction: TransactionSummary) => void;
}

const STATUS_BADGE_VARIANTS: Record<TransactionStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  COMPLETED: 'default',
  PENDING: 'secondary',
  FAILED: 'destructive',
  CANCELLED: 'outline',
};

const STATUS_LABELS: Record<TransactionStatus, string> = {
  COMPLETED: 'Completada',
  PENDING: 'Pendiente',
  FAILED: 'Fallida',
  CANCELLED: 'Cancelada',
};

const TYPE_LABELS: Record<TransactionType, string> = {
  CREDIT: 'Crédito',
  DEBIT: 'Débito',
  TRANSFER_IN: 'Transfer. entrada',
  TRANSFER_OUT: 'Transfer. salida',
  DEBIN: 'DEBIN',
  FEE: 'Comisión',
};

function formatMoney(amount: string, tipo: TransactionType): string {
  const num = parseFloat(amount);
  const isNegative = ['DEBIT', 'TRANSFER_OUT', 'FEE'].includes(tipo);
  const formatted = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(num);
  return isNegative ? `-${formatted}` : `+${formatted}`;
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}

export function AccountHistoryModal({
  account,
  open,
  onClose,
  onViewTransaction,
}: AccountHistoryModalProps) {
  const { t } = useTranslation(['accounts', 'common']);

  // Filtros locales
  const [transactionIdFilter, setTransactionIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<TransactionStatus | undefined>();
  const [page, setPage] = useState(0);

  // Query
  const { data, isLoading } = useTransactionHistory(
    account
      ? {
          accountId: account.id,
          page,
          size: 10,
          transactionId: transactionIdFilter || undefined,
          estado: statusFilter,
        }
      : null,
  );

  const handleStatusChange = useCallback((value: string) => {
    setStatusFilter(value === 'ALL' ? undefined : (value as TransactionStatus));
    setPage(0);
  }, []);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
  }, []);

  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t('accounts:history.title', 'Historial de Transacciones')}
          </DialogTitle>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
            <span>CVU: <code className="font-mono">{account.cvu}</code></span>
            <span>Merchant: {account.merchantRazonSocial}</span>
            <Badge variant="outline">{account.moneda}</Badge>
            <Badge variant={account.estado === 'ACTIVE' ? 'default' : 'secondary'}>
              {account.estado}
            </Badge>
          </div>
        </DialogHeader>

        {/* Filtros */}
        <div className="flex items-center gap-4 py-4 border-b">
          <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={transactionIdFilter}
                onChange={(e) => setTransactionIdFilter(e.target.value)}
                placeholder={t('accounts:history.searchPlaceholder', 'ID de transacción...')}
                className="pl-10"
              />
            </div>
            <Button type="submit" variant="secondary" size="sm">
              {t('common:search', 'Buscar')}
            </Button>
          </form>

          <Select value={statusFilter || 'ALL'} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos</SelectItem>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabla */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : data && data.content.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('accounts:history.columns.fecha', 'Fecha')}</TableHead>
                  <TableHead>{t('accounts:history.columns.id', 'ID')}</TableHead>
                  <TableHead>{t('accounts:history.columns.tipo', 'Tipo')}</TableHead>
                  <TableHead className="text-right">{t('accounts:history.columns.monto', 'Monto')}</TableHead>
                  <TableHead>{t('accounts:history.columns.estado', 'Estado')}</TableHead>
                  <TableHead className="text-right">{t('accounts:history.columns.acciones', 'Acciones')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.content.map((txn) => {
                  const isNegative = ['DEBIT', 'TRANSFER_OUT', 'FEE'].includes(txn.tipo);
                  return (
                    <TableRow key={txn.id}>
                      <TableCell className="text-muted-foreground">
                        {formatDate(txn.fecha)}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{txn.id}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {isNegative ? (
                            <ArrowDownCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <ArrowUpCircle className="h-4 w-4 text-green-500" />
                          )}
                          {TYPE_LABELS[txn.tipo]}
                        </div>
                      </TableCell>
                      <TableCell className={`text-right font-mono ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
                        {formatMoney(txn.monto, txn.tipo)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE_VARIANTS[txn.estado]}>
                          {STATUS_LABELS[txn.estado]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewTransaction(txn)}
                        >
                          {t('accounts:history.viewDetail', 'Visualizar')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              {t('accounts:history.empty', 'No se encontraron transacciones.')}
            </div>
          )}
        </div>

        {/* Paginación */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {page + 1} de {data.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= data.totalPages - 1}
            >
              Siguiente
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
